// SAMPLE 2K27-style build data — placeholder caps for the interface.
// Replace `base`/`hStep` and weight/wingspan effects with real in-game numbers later.
// Badge names ARE real NBA 2K27 badges (researched Sep 2026 launch list).

export const BUILDER_POSITIONS = ["PG", "SG", "SF", "PF", "C"];

// Height ladder in 1-inch steps: 5'9" (69") up to 7'4" (88").
const MIN_IN = 69, MAX_IN = 88;
export const HEIGHT_INCHES = [];
export const HEIGHTS = [];
for (let i = MIN_IN; i <= MAX_IN; i++) { HEIGHT_INCHES.push(i); HEIGHTS.push(`${Math.floor(i / 12)}'${i % 12}"`); }
export const MID_HEIGHT_IDX = HEIGHT_INCHES.indexOf(78); // 6'6" reference

// Per-position selectable height range (inches) — REAL 2K27 bounds from in-game builder.
const POS_H = { PG: [69, 79], SG: [72, 80], SF: [76, 82], PF: [77, 84], C: [79, 88] };
export const positionHeightRange = (pos) => {
  const [lo, hi] = POS_H[pos] || [MIN_IN, MAX_IN];
  return [HEIGHT_INCHES.indexOf(lo), HEIGHT_INCHES.indexOf(hi)];
};
export const clampHeightToPosition = (pos, idx) => {
  const [lo, hi] = positionHeightRange(pos);
  return Math.max(lo, Math.min(hi, idx));
};

// Weight ladder (lbs) and the selectable range for a given position + height.
export const WEIGHTS = Array.from({ length: 17 }, (_, i) => 160 + i * 10); // 160–320
export const weightRange = (position, heightIdx) => {
  const inches = HEIGHT_INCHES[heightIdx] ?? 78;
  let min = 170 + (inches - 75) * 5.6;
  let max = 225 + (inches - 75) * 8;
  const posAdj = { PG: -5, SG: 0, SF: 5, PF: 10, C: 15 }[position] || 0;
  min += posAdj * 0.4; max += posAdj;
  return [Math.max(160, Math.round(min / 10) * 10), Math.min(320, Math.round(max / 10) * 10)];
};
export const weightIdxRange = (position, heightIdx) => {
  const [lo, hi] = weightRange(position, heightIdx);
  return [WEIGHTS.indexOf(lo) === -1 ? 0 : WEIGHTS.indexOf(lo), WEIGHTS.indexOf(hi) === -1 ? WEIGHTS.length - 1 : WEIGHTS.indexOf(hi)];
};
export const clampWeight = (position, heightIdx, idx) => {
  const [lo, hi] = weightIdxRange(position, heightIdx);
  return Math.max(lo, Math.min(hi, idx));
};

// Wingspan measured in inches: from height (0) up to +6" (REAL 2K27 range).
export const WING_MIN_OFF = 0;
export const WING_MAX_OFF = 6;
export const WING_STEPS = WING_MAX_OFF - WING_MIN_OFF + 1; // 7
export const inchesToLabel = (inch) => `${Math.floor(inch / 12)}'${inch % 12}"`;
export const wingspanInches = (heightIdx, wingIdx) => (HEIGHT_INCHES[heightIdx] ?? 78) + WING_MIN_OFF + wingIdx;
export const wingT_of = (wingIdx) => (WING_STEPS > 1 ? wingIdx / (WING_STEPS - 1) : 0.5);

// category -> color + label
export const CATEGORIES = {
  finishing:   { label: "Finishing",   color: "#3B82F6" },
  shooting:    { label: "Shooting",    color: "#22C55E" },
  playmaking:  { label: "Playmaking",  color: "#F97316" },
  defense:     { label: "Defense / Rebounding", color: "#EF4444" },
  physical:    { label: "Physicals",   color: "#A855F7" },
};

// Each attribute: base cap (at mid height), hStep (cap change per height step above mid; negative = shrinks when taller)
export const ATTRIBUTES = [
  { key: "closeShot",   label: "Close Shot",      cat: "finishing", base: 85, hStep: 2 },
  { key: "drivingLayup",label: "Driving Layup",   cat: "finishing", base: 88, hStep: -3 },
  { key: "drivingDunk", label: "Driving Dunk",    cat: "finishing", base: 85, hStep: -2 },
  { key: "standingDunk",label: "Standing Dunk",   cat: "finishing", base: 55, hStep: 6 },
  { key: "postControl", label: "Post Control",    cat: "finishing", base: 60, hStep: 5 },

  { key: "midRange",    label: "Mid-Range Shot",  cat: "shooting",  base: 88, hStep: -3 },
  { key: "threePoint",  label: "Three-Point Shot",cat: "shooting",  base: 90, hStep: -4 },
  { key: "freeThrow",   label: "Free Throw",      cat: "shooting",  base: 90, hStep: -2 },

  { key: "passAccuracy",label: "Pass Accuracy",   cat: "playmaking",base: 88, hStep: -3 },
  { key: "ballHandle",  label: "Ball Handle",     cat: "playmaking",base: 90, hStep: -5 },
  { key: "speedWithBall",label: "Speed With Ball",cat: "playmaking",base: 88, hStep: -5 },

  { key: "interiorD",   label: "Interior Defense",cat: "defense",   base: 65, hStep: 5 },
  { key: "perimeterD",  label: "Perimeter Defense",cat: "defense",  base: 88, hStep: -3 },
  { key: "steal",       label: "Steal",           cat: "defense",   base: 85, hStep: -3 },
  { key: "block",       label: "Block",           cat: "defense",   base: 60, hStep: 6 },
  { key: "offReb",      label: "Offensive Rebound",cat: "defense",  base: 55, hStep: 6 },
  { key: "defReb",      label: "Defensive Rebound",cat: "defense",  base: 60, hStep: 6 },

  { key: "speed",       label: "Speed",           cat: "physical",  base: 90, hStep: -4 },
  { key: "agility",     label: "Agility",         cat: "physical",  base: 90, hStep: -4 },
  { key: "strength",    label: "Strength",        cat: "physical",  base: 60, hStep: 6 },
  { key: "vertical",    label: "Vertical",        cat: "physical",  base: 85, hStep: -1 },
  { key: "stamina",     label: "Stamina",         cat: "physical",  base: 90, hStep: 0 },
];

// Position bias: nudges a few caps by role (SAMPLE).
const POSITION_BIAS = {
  PG: { ballHandle: 4, speedWithBall: 4, passAccuracy: 4, threePoint: 3, interiorD: -6, block: -8, standingDunk: -8 },
  SG: { threePoint: 4, midRange: 3, perimeterD: 3, standingDunk: -4 },
  SF: { drivingDunk: 3, perimeterD: 2 },
  PF: { standingDunk: 5, interiorD: 4, defReb: 4, ballHandle: -4, threePoint: -3 },
  C:  { standingDunk: 8, interiorD: 8, block: 8, offReb: 6, defReb: 6, strength: 8, ballHandle: -10, speedWithBall: -8, threePoint: -6 },
};

// ---------------------------------------------------------------------------
// REAL 2K27 CAP DATA (owner-supplied). When a (position, height) cell exists
// here, the builder uses these EXACT in-game caps instead of the estimate math.
// Baseline = mid weight + Average wingspan; weight/wingspan then shift specific
// attributes via REAL_MODIFIERS (falls back to the estimate offsets below).
//
// HOW TO FILL: from the in-game MyPLAYER builder, for one position, set weight
// to mid + wingspan to Average, then read the cap of every attribute at each
// height. Example shape:
//   REAL_CAPS.PG["7'0\""] = { threePoint: 90, ballHandle: 78, block: 85, ... }
// ---------------------------------------------------------------------------
export const REAL_CAPS = {
  PG: {}, SG: {}, SF: {}, PF: {}, C: {},
};
// Optional: measured per-step change from wingspan/weight (overrides estimate).
export const REAL_MODIFIERS = {}; // e.g. REAL_MODIFIERS.PG = { wingspan: { threePoint: -2 }, weight: { strength: 8 } }

const clamp = (v, lo = 25, hi = 99) => Math.max(lo, Math.min(hi, Math.round(v)));

export const capFor = (attr, position, heightIdx, weightT, wingT) => {
  const w = (weightT - 0.5) * 2;   // -1..1
  const s = (wingT - 0.5) * 2;
  const wEff = { strength: 8, interiorD: 5, postControl: 4, standingDunk: 3, speed: -6, agility: -6, vertical: -5, speedWithBall: -3 };
  const sEff = { perimeterD: 6, steal: 4, block: 6, standingDunk: 4, interiorD: 3, midRange: -3, freeThrow: -2, threePoint: -2 };

  // 1) Use REAL owner data for this (position, height) when available.
  const realBase = REAL_CAPS[position]?.[HEIGHTS[heightIdx]]?.[attr.key];
  if (realBase != null) {
    const mods = REAL_MODIFIERS[position] || {};
    let cap = realBase;
    cap += ((mods.weight?.[attr.key]) ?? (wEff[attr.key] || 0)) * w;
    cap += ((mods.wingspan?.[attr.key]) ?? (sEff[attr.key] || 0)) * s;
    return clamp(cap);
  }

  // 2) Fallback: estimate math (SAMPLE — until real data is filled in).
  const inches = HEIGHT_INCHES[heightIdx] ?? 78;
  let cap = attr.base + attr.hStep * ((inches - 78) / 2);
  cap += (POSITION_BIAS[position]?.[attr.key] || 0);
  cap += (wEff[attr.key] || 0) * w;
  cap += (sEff[attr.key] || 0) * s;
  return clamp(cap);
};

// Badge tiers by attribute value (SAMPLE thresholds). 2K27 tokens cover Bronze→HOF;
// Legend is reached via the Synergy system in-game (shown here as the top tier).
export const TIERS = [
  { label: "Bronze", min: 60, color: "#B45309" },
  { label: "Silver", min: 70, color: "#CBD5E1" },
  { label: "Gold",   min: 80, color: "#F59E0B" },
  { label: "HOF",    min: 87, color: "#A855F7" },
  { label: "Legend", min: 93, color: "#FF2A55" },
];

// Total badges in 2K27 (for the "unlocked / 53" counter).
export const TOTAL_BADGES = 53;

export const badgeTier = (val) => {
  let cur = null;
  for (const t of TIERS) if (val >= t.min) cur = t;
  return cur;
};

// Next tier + points needed to reach it (null if already maxed).
export const nextTier = (val) => {
  for (const t of TIERS) if (val < t.min) return { ...t, needed: t.min - val };
  return null;
};

// ---------------------------------------------------------------------------
// REAL NBA 2K27 badges mapped to the attribute that primarily drives them.
// (Full 53-badge launch list, Sep 2026.) Free Throw has no badge in 2K.
// ---------------------------------------------------------------------------
export const BADGES_BY_ATTR = {
  closeShot:    ["Paint Prodigy", "Float Game", "Hook Specialist"],
  drivingLayup: ["Layup Mixmaster", "Ghost Stepper"],
  drivingDunk:  ["Posterizer", "Aerial Wizard"],
  standingDunk: ["Rise Up", "Physical Finisher"],
  postControl:  ["Post Powerhouse", "Post Spin Catalyst"],

  midRange:     ["Static Middy", "Post Fade Phenom", "Smooth Operator"],
  threePoint:   ["Limitless Range", "Deadeye", "Set and Fire", "Quick Trigger", "Arc Cadence", "Mini Marksman"],
  freeThrow:    [],

  passAccuracy: ["Dimer", "Bail Out", "Break Starter", "Versatile Visionary"],
  ballHandle:   ["Ankle Assassin", "Strong Handle", "Handles for Days", "Unpluckable"],
  speedWithBall:["Lightning Launch", "Pace"],

  interiorD:    ["Paint Patroller", "Post Lockdown", "Immovable Enforcer"],
  perimeterD:   ["Challenger", "Glove", "Off-Ball Pest", "Pick Dodger", "Seatbelt", "Wall Up", "Ankle Braces"],
  steal:        ["Interceptor"],
  block:        ["High-Flying Denier"],
  offReb:       ["Crasher", "Boxout Boss"],
  defReb:       ["Breaker", "Sync Snatcher", "Possession Closer"],

  speed:        ["Flash"],
  agility:      ["Slippery Off-Ball"],
  strength:     ["Brick Wall", "Bruiser"],
  vertical:     ["Pogo Stick"],
  stamina:      ["Work Horse"],
};

// Primary badge shown on each attribute row.
export const BADGE_NAMES = Object.fromEntries(
  Object.entries(BADGES_BY_ATTR).map(([k, v]) => [k, v[0] || null])
);

// badge name -> the attribute that drives it.
export const BADGE_ATTR = {};
Object.entries(BADGES_BY_ATTR).forEach(([attr, list]) => list.forEach((b) => { BADGE_ATTR[b] = attr; }));

// All badges grouped by category (for the "Possible Badges" explorer).
export const BADGES_IN_CATEGORY = {};
ATTRIBUTES.forEach((a) => { (BADGES_BY_ATTR[a.key] || []).forEach((b) => { (BADGES_IN_CATEGORY[a.cat] ||= []).push({ badge: b, attr: a.key }); }); });

// Per-badge attribute thresholds for each tier. REAL values captured from the
// in-game badge detail; anything missing falls back to the generic tier mins
// (est:true) until we record it. Send me a badge's four numbers to make it exact.
export const BADGE_REQS = {
  "Layup Mixmaster": { bronze: 70, silver: 83, gold: 90, hof: 99 },
};
export const badgeReqFor = (badge) => BADGE_REQS[badge] || { bronze: 60, silver: 70, gold: 80, hof: 87, est: true };

// Official 2K27 badge descriptions (what each badge does).
export const BADGE_DESC = {
  "Aerial Wizard": "Increases ability to finish alley-oop dunks and in-air putback dunks off offensive rebounds.",
  "Float Game": "Increases a player's ability to finish floaters.",
  "Layup Mixmaster": "Improves a player's ability to finish fancy or acrobatic layups.",
  "Paint Prodigy": "Improves ability to quickly and effectively score while working in the paint.",
  "Physical Finisher": "Improves ability to battle through contact and convert contact layups.",
  "Posterizer": "Increases the chances of dunking on your defender while driving to the hoop.",
  "Rise Up": "Increases the likelihood of dunking on your opponent when standing in the painted area.",
  "Hook Specialist": "Improves ability to make post hooks.",
  "Post Powerhouse": "Strengthens backing down defenders and moving them with drop steps.",
  "Post Spin Catalyst": "Post spins or drives are more effective with less chance to be stripped.",
  "Ghost Stepper": "Increases effectiveness of step-through layups or close shots after a shot fake.",
  "Post Fade Phenom": "Improves ability to make post fades and hop shots.",
  "Deadeye": "Jump shots taken with a defender closing out receive less penalty from a contest.",
  "Limitless Range": "Extends the range from which a player can shoot 3PT shots effectively.",
  "Mini Marksman": "Shorter shooters are less bothered by lengthy defenders.",
  "Set and Fire": "Shooting boost to 3-point shots when the player's feet are completely set.",
  "Arc Cadence": "Improves 3-point shots on the move — pull-ups, hop-steps, or spin shots.",
  "Static Middy": "Shooting boost to stand-still mid-range shots.",
  "Smooth Operator": "Improves mid-range shots on the move — pull-ups, hop-steps, or spin shots.",
  "Quick Trigger": "Improves ability to make no-dip jump shots.",
  "Bail Out": "Passing out of a jump shot or layup yields fewer errant passes; helps vs double teams.",
  "Break Starter": "Deep outlet passes made quickly after a defensive rebound are more accurate.",
  "Dimer": "Boosts the shot percentage for open teammates after catching a pass.",
  "Versatile Visionary": "Improves ability to thread tight passes and alley-oops quickly and on time.",
  "Ankle Assassin": "Increases the ability to break down the defender and cross them up.",
  "Handles for Days": "Less energy hit when performing consecutive dribble moves.",
  "Lightning Launch": "Speeds up launches when attacking from the perimeter.",
  "Strong Handle": "Reduces the likelihood of being bothered by defenders when dribbling.",
  "Unpluckable": "Defenders have a tougher time poking the ball free with steal attempts.",
  "Pace": "Raises the speed of the ball handler when sprinting.",
  "Off-Ball Pest": "Harder to get past off-ball — can grab and hold their matchup.",
  "High-Flying Denier": "Boosts speed and leaping for chase-down block attempts.",
  "Paint Patroller": "Increases ability to block or contest shots at the rim.",
  "Post Lockdown": "Strengthens defending post moves, with an increased chance of stripping.",
  "Wall Up": "Improves shot contest effectiveness during hands-up defense in the paint.",
  "Pick Dodger": "Improves navigating through and around screens on defense.",
  "Challenger": "Improves effectiveness of well-timed contests against perimeter shooters.",
  "Immovable Enforcer": "Improves defensive strength vs ball handlers and finishers coming straight at you.",
  "Ankle Braces": "Lowers the likelihood of getting ankle-broken by dribble moves.",
  "Seatbelt": "Boosts body-up defense — easier to clamp opponents and disrupt their drive.",
  "Glove": "Increases ability to steal from ball handlers or strip layup attempts.",
  "Interceptor": "Frequency of tipped or intercepted passes greatly increases.",
  "Crasher": "Boosts ability to track down or grab offensive rebounds.",
  "Possession Closer": "Boosts ability to grab defensive rebounds.",
  "Sync Snatcher": "Extra Vertical and Rebounding boost when perfectly timing the jump.",
  "Boxout Boss": "Improves the effectiveness of boxouts as the boxer.",
  "Breaker": "Increases the success rate of breaking through opponent boxouts.",
  "Slippery Off-Ball": "Navigate more effectively through traffic when getting open off screens.",
  "Pogo Stick": "Quickly go back up for another jump after a rebound, block, or shot.",
  "Work Horse": "Increases speed and ability to get loose balls over the opponent.",
  "Flash": "Speeds up off-ball movement in transition on offense or defense.",
  "Bruiser": "Drains energy from opponents when making physical contact.",
  "Brick Wall": "Increases the effectiveness of screens.",
};

// Tier a badge is at for a given driving-attribute cap (badge-specific reqs).
export const badgeTierByReq = (badge, capVal) => {
  const r = badgeReqFor(badge);
  if (capVal >= r.hof) return TIERS[3];
  if (capVal >= r.gold) return TIERS[2];
  if (capVal >= r.silver) return TIERS[1];
  if (capVal >= r.bronze) return TIERS[0];
  return null;
};

// ---------------------------------------------------------------------------
// CAP BREAKERS — permanent post-build upgrades that push an attribute beyond
// its cap. 2K27 allows a MAX of 5 per attribute; the gain per breaker is
// NON-LINEAR and inversely tied to the current rating. Numbers below use the
// community-tested scaling (2K26/2K27) — an ESTIMATE, since the true value is
// specific to each build's exact height/weight/wingspan (verify in-game).
// ---------------------------------------------------------------------------
export const CAP_BREAKER_MAX_PER_ATTR = 5;
export const CAP_BREAKER_POOL = 28; // total earnable across a MyPLAYER (community)

export const capBreakerGain = (rating) => {
  if (rating <= 40) return 10;
  if (rating <= 60) return 6;
  if (rating <= 75) return 3;
  if (rating <= 85) return 2;
  return 1;
};

// OWNER-SUPPLIED cap-breaker table. Key = the starting cap you break FROM.
// Value = the resulting rating after each of the (up to 5) breakers, in order.
// The gains depend ONLY on the starting rating, so this one table covers EVERY
// build/position/height. Example from in-game "Mock Cap Breakers":
//   CAP_BREAKER_TABLE[74] = [76, 77, 78, 79, 80];  // 74 -> +2,+1,+1,+1,+1
// Fill the rows you can read; anything missing falls back to the estimate curve.
export const CAP_BREAKER_TABLE = {};

// Project applying `count` cap breakers starting from `startValue` (usually the cap).
export const capBreakerProjection = (startValue, count) => {
  const key = Math.round(startValue);
  const row = CAP_BREAKER_TABLE[key];
  if (row && row.length >= count) {
    const steps = row.slice(0, count).map((v) => Math.min(99, v));
    const value = steps[steps.length - 1] ?? startValue;
    return { value, gain: value - startValue, steps, real: true };
  }
  let v = startValue;
  const steps = [];
  for (let i = 0; i < count; i++) {
    const gain = Math.min(capBreakerGain(v), 99 - v);
    v = Math.min(99, v + gain);
    steps.push(v);
  }
  return { value: v, gain: v - startValue, steps, real: false };
};

// ---------------------------------------------------------------------------
// BADGE TOKENS — 2K27 replaced fixed slots with a token economy. Each equipped
// badge costs tokens by tier. Costs are dynamic in-game (by height/position);
// these are representative values. BUDGET is a placeholder we'll tune together.
// ---------------------------------------------------------------------------
export const TIER_TOKEN_COST = { Bronze: 2, Silver: 4, Gold: 5, HOF: 6, Legend: 8 };
export const BADGE_TOKEN_BUDGET = 95; // real builds show ~90–99 badge pts (varies by build)
export const MAX_BADGE_SLOTS = 20;    // 20 base equip slots across the six disciplines
export const tokenCostForTier = (label) => TIER_TOKEN_COST[label] || 0;

// ---------------------------------------------------------------------------
// TAKEOVERS — unlocked by hitting attribute thresholds. Real 2K27 names +
// thresholds from the in-game builder. 18 total; rows below are confirmed so far.
// req.any => unlocked if ANY listed [attr, min] threshold is met.
// ---------------------------------------------------------------------------
export const TAKEOVERS = [
  { name: "Detonator",     req: { any: [["standingDunk", 85]] } },
  { name: "Shot Artist",   req: { any: [["threePoint", 89], ["midRange", 89]] } },
  { name: "Zip Code",      req: { any: [["threePoint", 91]] } },
  { name: "Cook",          req: { any: [["ballHandle", 90]] } },
  { name: "Hawk",          req: { any: [["steal", 85]] } },
  { name: "Second Chance", req: { any: [["offReb", 80]] } },
];
export const TOTAL_TAKEOVERS = 18;
export const takeoverUnlocked = (to, vals) => (to.req.any || []).some(([k, m]) => (vals?.[k] || 0) >= m);
export const takeoverReqLabel = (to, attrLabels) =>
  (to.req.any || []).map(([k, m]) => `${attrLabels[k] || k} ${m}`).join(" or ");
